import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { OfferPageActions, OfferPageRoom, TourV2MonthFilter } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { arrayToMap, groupBy, min, nonNullable, sortBy } from 'lib/array/arrayUtils'
import { getMonthShortName } from 'lib/datetime/dateUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { calculateTotalForDepartureByItems } from 'checkout/lib/utils/tours/priceView'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import extractTourV2PurchasableOptionDetails from 'lib/tours/extractTourV2PurchasableOptionDetails'
import TourV2YearNavigationBoxes from './TourV2YearNavigationBoxes'

const Tile = styled(Clickable)`
  position: relative;
  padding: ${rem(8)} ${rem(12)};
  text-align: center;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  height: ${rem(80)};
  min-width: ${rem(104)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: border-color 0.2s, background-color 0.2s;

  &:focus {
    ${interactiveFocusStyle}
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.one};
  }
`

const DiscountIconContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
`

interface Props {
  departures: Array<App.Tours.TourV2OfferDeparture>
  departureYear?: number
  departureMonth?: TourV2MonthFilter
  onSelect?: (month: number, year: number) => void
  travellers: Array<OfferPageRoom>
  tourItems?: Array<App.Checkout.TourV2Item>
  ref?: React.Ref<HTMLDivElement>
  offer?: App.Tours.TourV2Offer
}

function TourV2DepartureMonthsCarousel(props: Props) {
  const { departures, departureYear, onSelect, departureMonth, travellers, tourItems, ref, offer } = props

  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)

  const dispatch = useOfferPageDispatch()
  const monthViews = useMemo(() => {
    const departuresByMonth = groupBy(departures, dep => `${new Date(dep.startDate).getMonth()}-${new Date(dep.startDate).getFullYear()}`)
    const pricesByDeparture = arrayToMap(
      departures,
      departure => departure.id,
      departure => {
        const roomOptions = getDepartureOptionsForTravellers(departure, travellers)
        const cheapestOption = min(roomOptions.map(option => option.cheapestOption), option => {
          if (luxPlusEnabled) {
            return option.memberPrice || option.price
          }
          return option.price
        })

        const hasDiscount = roomOptions.flatMap(roomOption => roomOption.options)
          .some(option => extractTourV2PurchasableOptionDetails(option).hasDiscount)

        if (cheapestOption) {
          return {
            hasDiscount,
            price: luxPlusEnabled ? cheapestOption.memberPrice || cheapestOption.price : cheapestOption.price,
            type: (luxPlusEnabled && cheapestOption.memberPrice > 0) ? 'member' : 'default',
          }
        }
        return {
          hasDiscount: false,
          price: 0,
          type: 'default',
        }
      })

    // Find the cheapest price for this offer so we can let the tile know to highlight it
    const cheapestPrice = min(pricesByDeparture.values().filter(option => !!option.price).map(option => option.price).toArray())

    const months = departuresByMonth.entries().map(([key, departures]: [string, Array<App.Tours.TourV2OfferDeparture>]) => {
      const date = new Date(departures[0].startDate)
      const departurePrices = nonNullable(departures.map(dep => pricesByDeparture.get(dep.id)).filter(price => !!price?.price))
      const { price, type } = min(departurePrices, price => price.price) ?? { price: 0, type: 'default' }
      const hasDiscount = departurePrices.some(price => price.hasDiscount)

      // Find cheapest price on departures for this month compared to selected departure for same travellers/rooms
      const originalPrice = tourItems?.[0]?.originalTotal
      const totalDeparturePriceForCurrentItems = originalPrice ? departures.map(dep => calculateTotalForDepartureByItems(dep, tourItems, canRedeemLuxPlusBenefits)) : []
      const minTotalDeparturePrice = Math.min(...totalDeparturePriceForCurrentItems)
      const diffPrice = originalPrice ? minTotalDeparturePrice - originalPrice : 0

      return {
        id: key,
        date,
        year: date.getFullYear(),
        month: date.getMonth(),
        monthLabel: getMonthShortName(date),
        price: originalPrice ? (diffPrice > 0 ? diffPrice : 0) : price,
        cheapest: originalPrice ? false : price > 0 && price === cheapestPrice,
        isMemberPrice: originalPrice ? false : type === 'member',
        hasOriginalPrice: !!originalPrice,
        hasDiscount,
      }
    })
    return sortBy(months, month => new Date(month.date), 'asc')
  }, [departures, travellers, tourItems, canRedeemLuxPlusBenefits, luxPlusEnabled])

  const filteredOptions = useMemo(() => {
    if (departureYear) {
      return monthViews.filter(option => option.year === departureYear)
    }
    return monthViews
  }, [departureYear, monthViews])

  const onMonthClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const month = parseInt(e.currentTarget.dataset.month!)
    const year = parseInt(e.currentTarget.dataset.year!)
    onSelect?.(month, year)

    dispatch({ type: OfferPageActions.setDepartureMonth, month: { month, year } })
  }, [dispatch, onSelect])

  // if a single month has all the departures, don't show month picker carousel
  if (monthViews.length < 2) return

  return <CardCarousel gap={12} ref={ref} alignment="center" snap="start" gutterSize={0} gutterStyle="overflow">
    {/* Previous year navigation box */}
    {offer?.previousYearTour && (
      <TourV2YearNavigationBoxes
        linkTourDetails={{
          ...offer.previousYearTour,
          title: 'Looking for earlier dates?'
        }}
      />
    )}

    {/* Month tiles */}
    {filteredOptions.map(view => <Tile
      key={view.id}
      onClick={onMonthClick}
      data-year={view.year}
      data-month={view.month}
      className={cn({
        selected: departureMonth?.month === view.month && departureMonth.year === view.year,
      })}
    >
      {view.hasDiscount && <DiscountIconContainer>
        <SolidFireIcon colour="urgency" size="XS" />
      </DiscountIconContainer>}
      <BodyText variant="small" colour="neutral-three">{view.year}</BodyText>
      <BodyText variant="large">{view.monthLabel}</BodyText>
      <BodyText variant="large" weight="bold" colour={view.cheapest ? 'highlight-secondary' : view.isMemberPrice ? 'lux-plus' : undefined}>
        {(!!view.price || view.hasOriginalPrice) && <FormatCurrency signDisplay={view.hasOriginalPrice ? 'always' : 'never'} value={view.price} format="roundedDollar" />}
        {!view.price && !view.hasOriginalPrice && <Caption variant="small">SOLD OUT</Caption>}
      </BodyText>
    </Tile>)}

    {/* Next year navigation box */}
    {offer?.nextYearTour && (
      <TourV2YearNavigationBoxes
        linkTourDetails={{
          ...offer.nextYearTour,
          title: 'Booking for next year?'
        }}
      />
    )}
  </CardCarousel>
}

TourV2DepartureMonthsCarousel.displayName = 'TourV2DepartureMonthsCarousel'

type MappedProps = Pick<Props, 'travellers' | 'departureMonth' | 'departureYear' | 'offer'>

export default withOfferPageState((state): MappedProps => ({
  departureYear: state.departureYear,
  travellers: state.rooms,
  departureMonth: state.departureMonth,
  offer: state.tourV2Offer,
}), TourV2DepartureMonthsCarousel)
